/** MY WALL — the member's private investigation board.
 *  Pins reference canonical Archive objects; arrangement + notes persist
 *  server-side. Desktop: spatial board. Mobile: stacked cards. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pin, Trash2, Link2, X } from "lucide-react";
import { desk as dk, fontMicro } from "../components/member-desk/tokens";
import { getWall, patchPin, saveWallLayout, deletePin, createConnection, deleteConnection } from "./api";

const TYPE_STYLE = {
  dossier:  { label: "DOSSIER",  color: "#D92D20", bg: "#141210" },
  entity:   { label: "SUBJECT",  color: "#E0A526", bg: "#131211" },
  evidence: { label: "EVIDENCE", color: "#9FA8D4", bg: "#101114" },
  timeline: { label: "EVENT",    color: "#5DD4B3", bg: "#0f1312" },
};

const PinCard = ({ p, pins, onRemove, onNote, onConnect, dragProps, mobile, onReorder, idx, count }) => {
  const st = TYPE_STYLE[p.type] || TYPE_STYLE.dossier;
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(p.note || "");
  const [linking, setLinking] = useState(false);
  const [target, setTarget] = useState("");
  const [label, setLabel] = useState("");
  return (
    <div data-testid={`wall-pin-${p.id}`} {...(dragProps || {})}
      className="w-[230px] max-w-full border shadow-lg select-none"
      style={{ background: st.bg, borderColor: dk.edge, boxShadow: "0 8px 20px rgba(0,0,0,.5)" }}>
      {/* push-pin treatment */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b" style={{ borderColor: dk.edge }}>
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.color, boxShadow: `0 0 4px ${st.color}` }} />
        <span className="font-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: st.color }}>{st.label}</span>
        <span className="font-mono text-[8px] ml-auto" style={{ color: dk.inkMuted }}>{(p.pinned_at || "").slice(0, 10)}</span>
        <button onClick={() => onRemove(p.id)} data-testid={`wall-unpin-${p.id}`} aria-label="Remove pin"
          style={{ color: dk.inkMuted }}><Trash2 size={11} /></button>
      </div>
      <div className="p-2.5">
        {p.available ? (
          <Link to={p.route} data-testid={`wall-open-${p.id}`}
            className="block text-[12px] leading-snug hover:underline" style={{ color: dk.ink }}>
            {p.thumb && <img src={p.thumb} alt="" className="w-full h-20 object-cover mb-1.5 grayscale-[35%]" />}
            {p.title}
          </Link>
        ) : (
          <div className="text-[11px] italic" data-testid={`wall-unavailable-${p.id}`} style={{ color: dk.inkMuted }}>
            Record unavailable — it may have been withdrawn from the Archive.
          </div>
        )}
        {p.subtitle && <div className="font-mono text-[9px] uppercase mt-1" style={{ color: dk.inkMuted }}>{p.subtitle}</div>}
        {editing ? (
          <div className="mt-1.5">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} autoFocus
              data-testid={`wall-note-input-${p.id}`}
              className="w-full bg-black/40 text-[11px] p-1.5 outline-none resize-none"
              style={{ border: `1px solid ${dk.edgeHi}`, color: dk.ink }} />
            <button onClick={() => { onNote(p.id, note); setEditing(false); }} data-testid={`wall-note-save-${p.id}`}
              className={fontMicro} style={{ color: dk.go }}>Save note</button>
          </div>
        ) : (
          <div onClick={() => setEditing(true)} data-testid={`wall-note-${p.id}`}
            className="mt-1.5 text-[11px] italic cursor-text whitespace-pre-wrap"
            style={{ color: p.note ? "#CBB892" : dk.inkMuted }}>
            {p.note || "+ private note"}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          {!linking ? (
            <button onClick={() => setLinking(true)} data-testid={`wall-link-${p.id}`}
              className="font-mono text-[8px] uppercase tracking-[0.2em] flex items-center gap-1"
              style={{ color: dk.inkMuted }}><Link2 size={9} /> Connect</button>
          ) : (
            <div className="w-full space-y-1" data-testid={`wall-link-form-${p.id}`}>
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid={`wall-link-target-${p.id}`}
                className="w-full bg-black/50 text-[10px] font-mono p-1" style={{ border: `1px solid ${dk.edge}`, color: dk.ink }}>
                <option value="">— link to pin —</option>
                {pins.filter((x) => x.id !== p.id).map((x) => <option key={x.id} value={x.id}>{x.title?.slice(0, 40)}</option>)}
              </select>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="label (optional)"
                data-testid={`wall-link-label-${p.id}`}
                className="w-full bg-black/50 text-[10px] font-mono p-1" style={{ border: `1px solid ${dk.edge}`, color: dk.ink }} />
              <div className="flex gap-2">
                <button onClick={() => { if (target) onConnect(p.id, target, label); setLinking(false); setTarget(""); setLabel(""); }}
                  data-testid={`wall-link-save-${p.id}`} className={fontMicro} style={{ color: dk.go }}>Link</button>
                <button onClick={() => setLinking(false)} className={fontMicro} style={{ color: dk.inkMuted }}>Cancel</button>
              </div>
            </div>
          )}
          {mobile && (
            <span className="ml-auto flex gap-2">
              <button onClick={() => onReorder(p.id, -1)} disabled={idx === 0} className="disabled:opacity-20 font-mono text-[10px]" style={{ color: dk.inkDim }} data-testid={`wall-up-${p.id}`}>↑</button>
              <button onClick={() => onReorder(p.id, 1)} disabled={idx === count - 1} className="disabled:opacity-20 font-mono text-[10px]" style={{ color: dk.inkDim }} data-testid={`wall-down-${p.id}`}>↓</button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Wall({ onPinCountChange }) {
  const [wall, setWall] = useState(null);
  const boardRef = useRef(null);
  const drag = useRef(null);
  const saveTimer = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  const load = useCallback(() => getWall().then((w) => {
    setWall(w);
    onPinCountChange?.(w.pins.length);
  }).catch(() => setWall({ pins: [], connections: [] })), [onPinCountChange]);
  useEffect(() => { load(); }, [load]);

  const persistLayout = (pins) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveWallLayout(pins.map((p) => ({ id: p.id, x: p.x, y: p.y, order: p.order }))).catch(() => {});
    }, 700);
  };

  const onPointerDown = (e, pin) => {
    if (isMobile || e.target.closest("a,button,textarea,input,select")) return;
    const rect = boardRef.current.getBoundingClientRect();
    drag.current = { id: pin.id, rect };
    e.preventDefault();
  };
  useEffect(() => {
    const mv = (e) => {
      if (!drag.current) return;
      const { id, rect } = drag.current;
      const x = Math.max(0, Math.min(0.97, (e.clientX - rect.left - 60) / rect.width));
      const y = Math.max(0, Math.min(0.92, (e.clientY - rect.top - 15) / rect.height));
      setWall((w) => {
        const pins = w.pins.map((p) => p.id === id ? { ...p, x, y } : p);
        persistLayout(pins);
        return { ...w, pins };
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
  }, []);

  const remove = async (id) => {
    await deletePin(id).catch(() => toast.error("Remove failed"));
    load();
  };
  const note = async (id, text) => {
    await patchPin(id, { note: text }).catch(() => toast.error("Note failed"));
    setWall((w) => ({ ...w, pins: w.pins.map((p) => p.id === id ? { ...p, note: text } : p) }));
  };
  const connect = async (a, b, label) => {
    try { await createConnection(a, b, label); toast.success("Research connection saved (private)"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
  };
  const reorder = (id, dir) => {
    setWall((w) => {
      const pins = w.pins.slice().sort((a, b) => a.order - b.order);
      const i = pins.findIndex((p) => p.id === id);
      const j = i + dir;
      if (j < 0 || j >= pins.length) return w;
      [pins[i], pins[j]] = [pins[j], pins[i]];
      const next = pins.map((p, k) => ({ ...p, order: k }));
      persistLayout(next);
      return { ...w, pins: next };
    });
  };

  if (!wall) return <div className={fontMicro} style={{ color: dk.inkMuted }}>UNROLLING THE WALL…</div>;

  const pins = wall.pins.slice().sort((a, b) => a.order - b.order);
  const titleOf = (id) => pins.find((p) => p.id === id)?.title || "?";

  return (
    <div data-testid="my-wall">
      {pins.length === 0 ? (
        <div className="border border-dashed p-14 text-center" style={{ borderColor: dk.edgeHi }} data-testid="wall-empty">
          <Pin size={18} className="mx-auto mb-3" style={{ color: dk.inkMuted }} />
          <p className={fontMicro} style={{ color: dk.inkDim }}>Your Wall is clear.</p>
          <p className="text-[12px] mt-2 max-w-md mx-auto" style={{ color: dk.inkMuted }}>
            Pin evidence, people, documents or dossiers as you investigate.
          </p>
          <Link to="/archive" data-testid="wall-empty-cta"
            className={`${fontMicro} inline-block mt-4 border px-4 py-2`} style={{ borderColor: dk.edgeHi, color: dk.ink }}>
            Open the Archive →</Link>
        </div>
      ) : isMobile ? (
        <div className="space-y-3" data-testid="wall-mobile-list">
          {pins.map((p, i) => (
            <PinCard key={p.id} p={p} pins={pins} onRemove={remove} onNote={note} onConnect={connect}
              mobile onReorder={reorder} idx={i} count={pins.length} />
          ))}
        </div>
      ) : (
        <div ref={boardRef} data-testid="wall-board"
          className="relative w-full border overflow-hidden"
          style={{ height: "72vh", minHeight: 480, borderColor: dk.edgeHi,
                   background: "repeating-linear-gradient(45deg,#0c0b0e 0,#0c0b0e 14px,#0d0c10 14px,#0d0c10 28px)" }}>
          {pins.map((p) => (
            <div key={p.id} className="absolute touch-none"
              style={{ left: `${(p.x ?? 0.05) * 100}%`, top: `${(p.y ?? 0.05) * 100}%`, zIndex: drag.current?.id === p.id ? 30 : 10 }}
              onPointerDown={(e) => onPointerDown(e, p)}>
              <PinCard p={p} pins={pins} onRemove={remove} onNote={note} onConnect={connect} />
            </div>
          ))}
        </div>
      )}

      {(wall.connections || []).length > 0 && (
        <div className="mt-4 border p-3" style={{ borderColor: dk.edge }} data-testid="wall-connections">
          <div className={fontMicro} style={{ color: dk.gold }}>Member Research Connections — private, not Archive-verified</div>
          {wall.connections.map((c) => (
            <div key={c.id} className="flex items-center gap-2 py-1 text-[11px]" style={{ color: dk.inkDim }}
              data-testid={`wall-connection-${c.id}`}>
              <span style={{ color: dk.ink }}>{titleOf(c.a)}</span>
              <span className="font-mono text-[9px] px-1.5 border" style={{ borderColor: dk.edgeHi, color: "#CBB892" }}>
                {c.label || "possible connection"}</span>
              <span style={{ color: dk.ink }}>{titleOf(c.b)}</span>
              <button onClick={() => deleteConnection(c.id).then(load)} className="ml-auto" style={{ color: dk.inkMuted }}
                data-testid={`wall-connection-delete-${c.id}`}><X size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
